import { Component, h, Prop, Host, Element, Watch, Method } from '@stencil/core';
import { checkSlottedContentForInvalidHTML, hideFocusRingWhenUsingMouse } from '../../../global/utils/utils';
import linkSvg from '../../../assets/icon-chevron-right.svg';
export class LinkComponent {
  constructor() {
    /**
     * Determines the over-ride size of the text. By default it will inherit the font from the parent p tag
     */
    this.styledAs = 'inherit';
    /**
     * Determines the over-ride font colour
     */
    this.appearance = 'default';
    /**
     * The destination url for the navigation card click. To scroll to a given page element pass "#" and the id of the element eg "#mytextfield". Note this also sets
     * openNewWindow to false
     */
    this.href = '';
    /**
     * The offset from the top of the page, used when scrolling to the given element.
     */
    this.scrollToOffset = 160;
    /**
     * provide screen reader users with additional context or to convey information not available in HTML alone. Supplying this will override the default "Opens in new window"
     */
    this.srContext = '';
    /**
     * sets any aria attributes on the component
     */
    this.ariaAttribute = [];
    /**
     * Determines if the destination is opened in a new window
     */
    this.openNewWindow = true;
    /**
     * Callback function for onClick
     */
    this.callbackOnClick = null;
  }
  /**
   * Sets focus to the button
   */
  async setFocus() {
    this.inputElement.focus();
  }
  setAriaAttribute() {
    if (this.inputElement && this.ariaAttribute) {
      this.ariaAttribute.map((item) => {
        this.inputElement.setAttribute(item.attribute, item.value);
      });
    }
  }
  setSrContext(newValue) {
    if (newValue) {
      this.srContext = newValue;
    }
  }
  componentWillLoad() {
    checkSlottedContentForInvalidHTML(this.host, ['agl-ds-link', 'agl-ds-text']);
    this.setSrContext(this.srContext);
  }
  componentDidLoad() {
    hideFocusRingWhenUsingMouse(this.inputElement);
  }
  getElement(elementId, parent) {
    let element = parent.getElementById(elementId);
    if (!element) {
      // this is included to reduce the number of elements the function needs to recursively search through
      const els = parent.querySelectorAll(':not(meta):not(link):not(style):not(script):not(div):not(span):not(img):not(table):not(tr):not(td):not(th):not(agl-ds-text):not(agl-ds-p):not(agl-ds-spacer)');
      for (let e = 0; e < els.length; e++) {
        if (els[e].shadowRoot) {
          if (els[e].shadowRoot.getElementById(elementId)) {
            element = els[e].shadowRoot.getElementById(elementId);
            return element;
          }
          else {
            element = this.getElement(elementId, els[e].shadowRoot);
            if (element) {
              return element;
            }
          }
        }
      }
    }
    return element;
  }
  doScrolling(elementId) {
    if (elementId === '')
      return false;
    const duration = 700;
    const startingY = window.pageYOffset;
    const element = this.getElement(elementId, document);
    if (!element) {
      throw new Error(`${elementId} is not a valid element id to use with the agl-ds-link 'scroll to' feature`);
    }
    const elementY = window.pageYOffset + element.getBoundingClientRect().top;
    const diff = elementY - startingY - this.scrollToOffset;
    let start = 0;
    // Bootstrap our animation - it will get called right before next frame shall be rendered.
    window.requestAnimationFrame(function step(timestamp) {
      if (!start)
        start = timestamp;
      // Elapsed milliseconds since start of scrolling.
      const time = timestamp - start;
      // Get percent of completion in range [0, 1].
      const percent = Math.min(time / duration, 1);
      window.scrollTo(0, startingY + diff * percent);
      // Proceed with animation as long as we wanted it to.
      if (time < duration) {
        window.requestAnimationFrame(step);
      }
      else {
        if (element.setFocus) {
          element.setFocus();
        }
        else {
          element.focus();
        }
      }
    });
  }
  render() {
    const isIdHref = this.href.toString().startsWith('#');
    const href = isIdHref ? 'javascript:void(0)' : this.href;
    const openNewWindow = isIdHref ? false : this.openNewWindow;
    if (!['display01', 'display02', 'inherit', 'lg', 'md', 'sm', 'xl', 'xs'].includes(this.styledAs)) {
      throw new Error(this.styledAs + ' is not a valid font size');
    }
    if (!['default', 'inverse', 'secondary'].includes(this.appearance)) {
      throw new Error(this.appearance + ' is not a valid font colour');
    }
    return (h(Host, null,
      h("a", { ref: (el) => (this.inputElement = el), rel: openNewWindow ? 'noopener noreferrer' : '', target: openNewWindow ? '_blank' : '_self', class: {
          ['agl-ds-' + this.styledAs]: true,
          ['agl-ds-a-' + this.appearance + '-colour']: true
        }, href: href, onClick: (e) => {
          if (isIdHref) {
            const idToScrollTo = this.href.replace('#', '');
            this.doScrolling(idToScrollTo);
          }
          if (this.callbackOnClick) {
            this.callbackOnClick(e);
            return true;
          }
        } },
        this.srContext ? h("span", { class: "sr-only" },
          " ",
          this.srContext) : '',
        this.openNewWindow ? h("span", { class: "sr-only" }, ", opens in new window") : '',
        h("slot", null),
        this.appearance === 'secondary' && h("span", { innerHTML: linkSvg }))));
  }
  static get is() { return "agl-ds-link"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() { return {
    "$": ["link.component.scss"]
  }; }
  static get styleUrls() { return {
    "$": ["link.component.css"]
  }; }
  static get properties() { return {
    "styledAs": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "FontSizes",
        "resolved": "\"display01\" | \"display02\" | \"inherit\" | \"lg\" | \"md\" | \"sm\" | \"xl\" | \"xs\"",
        "references": {
          "FontSizes": {
            "location": "import",
            "path": "../../../global/component.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Determines the over-ride size of the text. By default it will inherit the font from the parent p tag"
      },
      "attribute": "styled-as",
      "reflect": false,
      "defaultValue": "'inherit'"
    },
    "appearance": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "LinkAppearance",
        "resolved": "\"default\" | \"inverse\" | \"secondary\"",
        "references": {
          "LinkAppearance": {
            "location": "import",
            "path": "./link.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Determines the over-ride font colour"
      },
      "attribute": "appearance",
      "reflect": false,
      "defaultValue": "'default'"
    },
    "href": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The destination url for the navigation card click. To scroll to a given page element pass \"#\" and the id of the element eg \"#mytextfield\". Note this also sets\nopenNewWindow to false"
      },
      "attribute": "href",
      "reflect": false,
      "defaultValue": "''"
    },
    "scrollToOffset": {
      "type": "number",
      "mutable": false,
      "complexType": {
        "original": "number",
        "resolved": "number",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The offset from the top of the page, used when scrolling to the given element."
      },
      "attribute": "scroll-to-offset",
      "reflect": false,
      "defaultValue": "160"
    },
    "srContext": {
      "type": "string",
      "mutable": true,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "provide screen reader users with additional context or to convey information not available in HTML alone. Supplying this will override the default \"Opens in new window\""
      },
      "attribute": "sr-context",
      "reflect": false,
      "defaultValue": "''"
    },
    "ariaAttribute": {
      "type": "unknown",
      "mutable": false,
      "complexType": {
        "original": "AriaAttributes[]",
        "resolved": "AriaAttributes[]",
        "references": {
          "AriaAttributes": {
            "location": "import",
            "path": "../../../global/component.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "sets any aria attributes on the component"
      },
      "defaultValue": "[]"
    },
    "openNewWindow": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Determines if the destination is opened in a new window"
      },
      "attribute": "open-new-window",
      "reflect": false,
      "defaultValue": "true"
    },
    "callbackOnClick": {
      "type": "unknown",
      "mutable": false,
      "complexType": {
        "original": "(event: MouseEvent) => void",
        "resolved": "(event: MouseEvent) => void",
        "references": {
          "MouseEvent": {
            "location": "global"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Callback function for onClick"
      },
      "defaultValue": "null"
    }
  }; }
  static get methods() { return {
    "setFocus": {
      "complexType": {
        "signature": "() => Promise<void>",
        "parameters": [],
        "references": {
          "Promise": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Sets focus to the button",
        "tags": []
      }
    }
  }; }
  static get elementRef() { return "host"; }
  static get watchers() { return [{
      "propName": "ariaAttribute",
      "methodName": "setAriaAttribute"
    }, {
      "propName": "srContext",
      "methodName": "setSrContext"
    }]; }
}
